/** Painel do Void Altar (upgrades) + prompt "E — Interagir" de proximidade. */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { type EssenceType, ESSENCE_TYPES, ESSENCE_COLOR, ESSENCE_I18N } from "../essence/EssenceTypes";
import type { VoidAltar } from "../world/VoidAltar";
import type { EssenceInventory } from "../essence/EssenceInventory";

function hex(color: number): string {
  return "#" + color.toString(16).padStart(6, "0");
}

/** Valor exibido no efeito de cada upgrade (Damage=1, MoveSpeed=1%, Crit=0.5% ...). */
function effectValue(key: EssenceType, amount: number): number {
  if (key === "agility") return Math.round((amount / GameConfig.player.moveSpeed) * 100);
  if (key === "ferocity") return Math.round(amount * 100 * 10) / 10;
  return amount;
}

export class AltarPanel {
  private readonly root: HTMLDivElement;
  private readonly rows: HTMLDivElement;
  private readonly titleEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly prompt: HTMLDivElement;

  private altar: VoidAltar | null = null;
  private inventory: EssenceInventory | null = null;

  constructor(uiRoot: HTMLElement, private readonly onClose: () => void, private readonly onTree?: () => void) {
    this.prompt = document.createElement("div");
    this.prompt.className = "altar-prompt";
    this.prompt.hidden = true;
    uiRoot.appendChild(this.prompt);

    this.root = document.createElement("div");
    this.root.className = "overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel altar-panel">
        <h2></h2>
        <div class="altar-rows"></div>
        <div class="altar-actions">
          <button class="btn altar-tree" data-a="tree">✦</button>
          <button class="btn primary" data-a="close"></button>
        </div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.titleEl = this.root.querySelector("h2")!;
    this.rows = this.root.querySelector(".altar-rows")!;
    this.closeBtn = this.root.querySelector('[data-a="close"]')!;
    this.closeBtn.onclick = () => this.onClose();
    const treeBtn = this.root.querySelector<HTMLButtonElement>('[data-a="tree"]');
    if (treeBtn) treeBtn.onclick = () => this.onTree?.();

    i18n.onChange(() => {
      this.prompt.textContent = i18n.t("altar.interact");
      if (!this.root.hidden) this.render();
    });
    this.prompt.textContent = i18n.t("altar.interact");
  }

  setPromptVisible(v: boolean): void {
    this.prompt.hidden = !v;
  }

  open(altar: VoidAltar, inventory: EssenceInventory): void {
    this.altar = altar;
    this.inventory = inventory;
    this.setPromptVisible(false);
    this.render();
    this.root.hidden = false;
  }

  close(): void {
    this.root.hidden = true;
  }

  private render(): void {
    if (!this.altar || !this.inventory) return;
    const altar = this.altar;
    const inv = this.inventory;
    this.titleEl.textContent = i18n.t("altar.name");
    this.closeBtn.textContent = i18n.t("altar.close");
    const treeBtn = this.root.querySelector<HTMLButtonElement>('[data-a="tree"]');
    if (treeBtn) treeBtn.textContent = `✦ ${i18n.t("voidTree.title")}`;
    this.rows.innerHTML = "";

    for (const key of ESSENCE_TYPES) {
      const def = altar.defFor(key);
      const cost = altar.costFor(key);
      const have = inv.get(key);
      const afford = have >= cost;
      const effect = i18n.t(def.descKey, { n: effectValue(key, def.amount) });

      const row = document.createElement("div");
      row.className = "altar-row";
      row.innerHTML = `
        <div class="ar-info">
          <span class="ar-name" style="color:${hex(ESSENCE_COLOR[key])}">
            ${i18n.t(ESSENCE_I18N[key])} <span class="ar-lv">${i18n.t("altar.level", { n: altar.levels[key] })}</span>
          </span>
          <span class="ar-effect">${effect}</span>
        </div>
        <div class="ar-buy">
          <span class="ar-have">${have} / ${cost}</span>
          <button class="btn ar-btn" ${afford ? "" : "disabled"}>${i18n.t("altar.cost")}: ${cost}</button>
        </div>`;
      const btn = row.querySelector("button")!;
      btn.onclick = () => {
        if (altar.buy(key)) this.render();
      };
      this.rows.appendChild(row);
    }
  }
}
